import { apiFetch } from '../lib/api';
import { useState, useCallback, useRef, useMemo } from 'react';
import type { LocationLink, ClientMessage, ServerMessage } from '@roll1/shared';
import type { DistributiveOmit } from './useMultiplayer';
import { useI18n } from '../i18n';
import type { PendingEdit } from '../contexts/ImageGalleryContext';


// ── Types ────────────────────────────────────────────────────────

export interface DetailRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DetailPassState {
  region: DetailRegion | null;
  cropPreview: string | null;
  prompt: string;
  semanticDescription: string | null;
  generatedUrl: string | null;
  overlappingLinks: LocationLink[];
  generating: boolean;
  accepting: boolean;
  error: string | null;
  dialogOpen: boolean;
}

const INITIAL_STATE: DetailPassState = {
  region: null,
  cropPreview: null,
  prompt: '',
  semanticDescription: null,
  generatedUrl: null,
  overlappingLinks: [],
  generating: false,
  accepting: false,
  error: null,
  dialogOpen: false,
};

export interface UseDetailPassReturn extends DetailPassState {
  setRegion: (region: DetailRegion) => void;
  setPrompt: (prompt: string) => void;
  setSemanticDescription: (desc: string) => void;
  generate: () => Promise<void>;
  accept: (options?: { regenerateExisting?: LocationLink }) => Promise<void>;
  reset: () => void;
  closeDialog: () => void;
}

interface UseDetailPassProps {
  sessionId: string;
  participantId: string;
  pageId: string;
  mapUrl: string | null;
  /** Map scale factor — world coords = pixel coords × mapScale */
  mapScale?: number;
  /** F-8: the picture is shifted against the grid by this many image pixels. */
  mapOffset?: { x: number; y: number };
  locationLinks: LocationLink[];
  send: (msg: DistributiveOmit<ClientMessage, 'id' | 'timestamp'>) => void;
  onServerMessage: (handler: (msg: ServerMessage) => void) => () => void;
  navigateToPage: (pageId: string) => void;
  /** Group Q review item 2: the job runs on its own — the dialog closes, the result waits in the compare window. */
  registerEdit?: (edit: PendingEdit) => void;
}

// ── Utilities (exported for testing) ─────────────────────────────

/** Crop preview via Canvas API */
export function cropPreviewFromCanvas(
  mapUrl: string,
  region: DetailRegion,
  previewSize: number = 400,
  t?: (key: any) => string,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const aspect = region.width / region.height;
        canvas.width = aspect >= 1 ? previewSize : Math.round(previewSize * aspect);
        canvas.height = aspect >= 1 ? Math.round(previewSize / aspect) : previewSize;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error(t?.('hookError.canvasContext') ?? 'Canvas 2d context unavailable')); return; }
        ctx.drawImage(img, region.x, region.y, region.width, region.height, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error(t?.('hookError.mapLoadFailed') ?? 'Failed to load map image for crop preview'));
    img.src = mapUrl;
  });
}

/** Find LocationLinks that overlap with the selection by >= 50% of link area */
export function findOverlappingLinks(
  links: LocationLink[],
  selection: DetailRegion,
): LocationLink[] {
  return links.filter(link => {
    if (link.region.type !== 'rect') return false;
    const r = link.region;
    // Intersection area
    const ix = Math.max(selection.x, r.x);
    const iy = Math.max(selection.y, r.y);
    const iw = Math.min(selection.x + selection.width, r.x + r.width) - ix;
    const ih = Math.min(selection.y + selection.height, r.y + r.height) - iy;
    if (iw <= 0 || ih <= 0) return false;
    const overlapArea = iw * ih;
    const linkArea = r.width * r.height;
    return overlapArea / linkArea >= 0.5;
  });
}

/** Wait for page:created server message */
function waitForPageCreated(
  onServerMessage: UseDetailPassProps['onServerMessage'],
  timeout: number = 10_000,
  t?: (key: any) => string,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unsub();
      reject(new Error(t?.('hookError.pageCreateTimeout') ?? 'Timeout waiting for page:created'));
    }, timeout);
    const unsub = onServerMessage((msg: ServerMessage) => {
      if (msg.type === 'page:created') {
        clearTimeout(timer);
        unsub();
        resolve(msg.page.id);
      }
    });
  });
}

// ── Hook ─────────────────────────────────────────────────────────

/** Convert a world-space region to pixel-space: divide by mapScale, then take the picture's shift off (F-8). */
export function worldToPixelRegion(region: DetailRegion, mapScale: number, offset: { x: number; y: number } = { x: 0, y: 0 }): DetailRegion {
  if (mapScale === 1 && offset.x === 0 && offset.y === 0) return region;
  return {
    x: region.x / mapScale - offset.x,
    y: region.y / mapScale - offset.y,
    width: region.width / mapScale,
    height: region.height / mapScale,
  };
}

export function useDetailPass(props: UseDetailPassProps): UseDetailPassReturn {
  const { sessionId, participantId, pageId, mapUrl, mapScale = 1, mapOffset, locationLinks, send, onServerMessage, navigateToPage, registerEdit } = props;
  const { t } = useI18n();
  const [state, setState] = useState<DetailPassState>(INITIAL_STATE);
  const abortRef = useRef<AbortController | null>(null);

  const setRegion = useCallback((region: DetailRegion) => {
    // Compute overlapping links
    const overlappingLinks = findOverlappingLinks(locationLinks, region);

    setState(s => ({
      ...s,
      region,
      overlappingLinks,
      generatedUrl: null,
      semanticDescription: null,
      error: null,
      dialogOpen: true,
    }));

    // Compute crop preview async (convert world coords to pixel coords for canvas crop)
    if (mapUrl) {
      cropPreviewFromCanvas(mapUrl, worldToPixelRegion(region, mapScale, mapOffset), 400, t).then(
        (preview) => setState(s => ({ ...s, cropPreview: preview })),
        (err) => { console.warn('[detail-pass] crop preview failed:', err, { mapScale, region: worldToPixelRegion(region, mapScale, mapOffset) }); },
      );
    }
  }, [locationLinks, mapUrl, mapScale, mapOffset?.x, mapOffset?.y, t]);

  const setPrompt = useCallback((prompt: string) => {
    setState(s => ({ ...s, prompt }));
  }, []);

  const setSemanticDescription = useCallback((desc: string) => {
    setState(s => ({ ...s, semanticDescription: desc }));
  }, []);

  /** Put a detail result: on the map of the overlapping link, or as a new map linked from this one. */
  const acceptResult = useCallback(async (url: string, region: DetailRegion, prompt: string, regenerateExisting?: LocationLink): Promise<void> => {
    const label = prompt.trim().slice(0, 50) || `Detail: page`;
    if (regenerateExisting) {
      send({ type: 'map:generation-accept', pageId: regenerateExisting.targetPageId, url, clearDrawings: true });
      navigateToPage(regenerateExisting.targetPageId);
      return;
    }
    const pageCreatedPromise = waitForPageCreated(onServerMessage, 10_000, t);
    send({ type: 'page:create', name: label, parentPageId: pageId });
    const newPageId = await pageCreatedPromise;
    send({ type: 'map:generation-accept', pageId: newPageId, url, clearDrawings: true });
    send({ type: 'location:link-create', pageId, targetPageId: newPageId, label, region: { type: 'rect', ...region }, style: 'area' });
    navigateToPage(newPageId);
  }, [pageId, send, onServerMessage, navigateToPage, t]);

  const generate = useCallback(async () => {
    if (!state.region || !state.prompt.trim()) return;

    setState(s => ({ ...s, generating: true, error: null }));

    try {
      const formData = new FormData();
      formData.append('mode', 'detail');
      formData.append('prompt', state.prompt.trim());
      formData.append('cropRegion', JSON.stringify(worldToPixelRegion(state.region, mapScale, mapOffset)));
      formData.append('pageId', pageId);
      formData.append('parentPageId', pageId);
      formData.append('width', '2000');
      formData.append('height', '1500');
      if (state.semanticDescription) {
        formData.append('semanticDescription', state.semanticDescription);
      }

      abortRef.current = new AbortController();

      const res = await apiFetch(
        `/api/generate-map?sessionId=${sessionId}&participantId=${participantId}`,
        { method: 'POST', body: formData, signal: abortRef.current.signal },
      );

      const data = await res.json() as {
        ok: boolean;
        url?: string;
        imageId?: string;
        error?: string;
        semanticAnalysis?: { description: string };
      };

      if (data.ok && data.imageId && !data.url && registerEdit) {
        // Started (202): the dialog closes; «Готово — сравнить» → a new map, or the map of the overlapping link.
        const region = state.region;
        const prompt = state.prompt;
        const overlap = state.overlappingLinks[0];
        registerEdit({
          imageId: data.imageId,
          title: t('images.edit.detail').replace('{name}', prompt.trim().slice(0, 40)),
          beforeUrl: state.cropPreview,
          actions: [
            { label: t('images.edit.acceptDetail'), run: (url) => acceptResult(url, region, prompt) },
            ...(overlap ? [{ label: t('images.edit.replaceLinked').replace('{name}', overlap.label), run: (url: string) => acceptResult(url, region, prompt, overlap) }] : []),
          ],
        });
        setState(INITIAL_STATE);
      } else if (data.ok && data.url) {
        setState(s => ({
          ...s,
          generatedUrl: data.url!,
          generating: false,
          semanticDescription: data.semanticAnalysis?.description ?? s.semanticDescription,
        }));
      } else {
        setState(s => ({ ...s, generating: false, error: data.error ?? t('hookError.generationFailed') }));
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setState(s => ({ ...s, generating: false }));
        return;
      }
      setState(s => ({ ...s, generating: false, error: t('hookError.networkError') }));
    }
  }, [state.region, state.prompt, state.semanticDescription, state.overlappingLinks, state.cropPreview, pageId, sessionId, participantId, mapScale, mapOffset?.x, mapOffset?.y, t, registerEdit, acceptResult]);

  const accept = useCallback(async (options?: { regenerateExisting?: LocationLink }) => {
    if (!state.generatedUrl || !state.region) return;

    setState(s => ({ ...s, accepting: true, error: null }));

    try {
      const label = state.prompt.trim().slice(0, 50) || `Detail: page`;

      if (options?.regenerateExisting) {
        // Regenerate existing page map
        send({
          type: 'map:generation-accept',
          pageId: options.regenerateExisting.targetPageId,
          url: state.generatedUrl,
          clearDrawings: true,
        });
        navigateToPage(options.regenerateExisting.targetPageId);
      } else {
        // Create new page + link
        const pageCreatedPromise = waitForPageCreated(onServerMessage, 10_000, t);

        send({
          type: 'page:create',
          name: label,
          parentPageId: pageId,
        });

        const newPageId = await pageCreatedPromise;

        send({
          type: 'map:generation-accept',
          pageId: newPageId,
          url: state.generatedUrl,
          clearDrawings: true,
        });

        send({
          type: 'location:link-create',
          pageId,
          targetPageId: newPageId,
          label,
          region: { type: 'rect', ...state.region },
          style: 'area',
        });

        navigateToPage(newPageId);
      }

      setState(INITIAL_STATE);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setState(s => ({ ...s, accepting: false, error: message }));
    }
  }, [state.generatedUrl, state.region, state.prompt, pageId, send, onServerMessage, navigateToPage, t]);

  const reset = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setState(INITIAL_STATE);
  }, []);

  const closeDialog = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setState(INITIAL_STATE);
  }, []);

  return useMemo(() => ({
    ...state,
    setRegion,
    setPrompt,
    setSemanticDescription,
    generate,
    accept,
    reset,
    closeDialog,
  }), [
    state, setRegion, setPrompt, setSemanticDescription,
    generate, accept, reset, closeDialog,
  ]);
}
